/**
 * AllysListingsMapView - Split-pane map view styled to match the Allys mailer aesthetic
 *
 * Layout mirrors the mailer district map generator:
 *  - Left sidebar: clean white panel with subtle borders, listing cards with indicators
 *  - Right panel: full-height Leaflet map with grayscale CartoDB tiles
 *
 * Bidirectional sync between sidebar cards and map markers.
 */

'use client';

import { useState, useCallback, useRef, useMemo } from 'react';
import dynamic from 'next/dynamic';
import type { ListingCardData } from '@/components/listings/types';
import { AllysListingMapCard } from './AllysListingMapCard';

const AllysLeafletMapClient = dynamic(
  () => import('./AllysLeafletMapClient'),
  { ssr: false, loading: () => <MapSkeleton /> },
);

function MapSkeleton() {
  return (
    <div className="w-full h-full bg-[#f0f5f7] animate-pulse flex items-center justify-center">
      <div className="text-center">
        <svg className="w-12 h-12 mx-auto mb-3 text-[#c8d3d8]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
        <p className="text-xs text-[#9ca3af] font-medium tracking-wide">Loading map&hellip;</p>
      </div>
    </div>
  );
}

export interface AllysListingsMapViewProps {
  listings: ListingCardData[];
  basePath: string;
  locale?: string;
  messages?: Record<string, unknown>;
  height?: string;
  className?: string;
}

export function AllysListingsMapView({
  listings,
  basePath,
  locale = 'en',
  messages,
  height = 'calc(100vh - 180px)',
  className = '',
}: AllysListingsMapViewProps) {
  const [selectedListingId, setSelectedListingId] = useState<string | null>(null);
  const [hoveredListingId, setHoveredListingId] = useState<string | null>(null);
  const [panToListingId, setPanToListingId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const listingsWithCoords = useMemo(
    () => listings.filter(l => l.location?.latitude != null && l.location?.longitude != null),
    [listings],
  );

  const hasCoords = useCallback(
    (listing: ListingCardData) => listing.location?.latitude != null && listing.location?.longitude != null,
    [],
  );

  const handleCardHover = useCallback((listingId: string | null, listing?: ListingCardData) => {
    setHoveredListingId(listingId);
    if (listing && hasCoords(listing)) {
      setPanToListingId(listingId);
    }
  }, [hasCoords]);

  const handleCardClick = useCallback((listing: ListingCardData) => {
    setSelectedListingId(listing.id);
    if (hasCoords(listing)) {
      setPanToListingId(listing.id);
    }
  }, [hasCoords]);

  const handleMarkerClick = useCallback((listing: ListingCardData) => {
    setSelectedListingId(listing.id);
    const el = document.getElementById(`map-listing-${listing.id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  if (listings.length === 0) {
    return (
      <div
        className="flex items-center justify-center py-20 text-center"
        style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
      >
        <div>
          <svg className="w-14 h-14 mx-auto mb-4 text-[#c8d3d8]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          <h3 className="text-base font-semibold text-[#1a3540] mb-1">No listings</h3>
          <p className="text-sm text-[#6b7280]">Try adjusting your filters.</p>
        </div>
      </div>
    );
  }

  const emptyMsg = messages as Record<string, unknown> | undefined;
  const emptyTitle = typeof emptyMsg?.emptyMapTitle === 'string' ? emptyMsg.emptyMapTitle : 'No properties with location data';
  const emptyDesc = typeof emptyMsg?.emptyMapDescription === 'string' ? emptyMsg.emptyMapDescription : 'Location coordinates are not available for these listings.';

  return (
    <div
      className={`flex flex-col lg:flex-row ${className}`}
      style={{ height, fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
    >
      {/* Left panel: sidebar card list */}
      <div
        ref={listRef}
        className="w-full lg:w-[320px] xl:w-[340px] overflow-y-auto bg-white border-r border-[#e8ebef] flex-shrink-0"
      >
        {/* Results count header */}
        <div className="sticky top-0 z-10 bg-white border-b border-[#e8ebef] px-5 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6b7280]">
            <span className="text-[#2D5A6B] font-bold">{listings.length}</span>
            {' '}{listings.length === 1 ? 'property' : 'properties'}
            {listingsWithCoords.length > 0 && listingsWithCoords.length < listings.length && (
              <span className="text-[#c8d3d8] font-normal normal-case tracking-normal">
                {' '}&middot; {listingsWithCoords.length} on map
              </span>
            )}
          </p>
        </div>

        <div className="p-3 space-y-1.5">
          {listings.map((listing) => (
            <div key={listing.id} id={`map-listing-${listing.id}`}>
              <AllysListingMapCard
                listing={listing}
                href={`${basePath}/properties/${listing.property.id}`}
                isSelected={selectedListingId === listing.id}
                isHovered={hoveredListingId === listing.id}
                onMouseEnter={() => handleCardHover(listing.id, listing)}
                onMouseLeave={() => handleCardHover(null)}
                onClick={() => handleCardClick(listing)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Right panel: map */}
      <div className="w-full lg:flex-1 h-full relative">
        {listingsWithCoords.length > 0 ? (
          <AllysLeafletMapClient
            listings={listingsWithCoords}
            selectedListingId={selectedListingId}
            hoveredListingId={hoveredListingId}
            panToListingId={panToListingId}
            onMarkerClick={handleMarkerClick}
            basePath={basePath}
            locale={locale}
            className="w-full h-full"
          />
        ) : (
          <div className="w-full h-full bg-[#f0f5f7] flex items-center justify-center">
            <div className="text-center px-8">
              <svg className="w-14 h-14 mx-auto mb-4 text-[#c8d3d8]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
              </svg>
              <h3 className="text-base font-semibold text-[#1a3540] mb-1">{emptyTitle}</h3>
              <p className="text-sm text-[#6b7280]">{emptyDesc}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AllysListingsMapView;
